import mustache from "mustache";
import { serviceProvider } from "../../../../comun/provider/ServiceProvider";
import { ParametrosMedicion } from "../../../../modulos/aplicaciones/calculoAplicacion";
import { AplicacionRequest } from "../../../../modulos/aplicaciones/request/AplicacionRequest";
import { AplicacionRecipienteResponse } from "../../../../modulos/aplicaciones/response/AplicacionRecipienteResponse";
import { fx_createAplicacionRecipienteGrpcService } from "../../../../modulos/aplicaciones/services/AplicacionRecipienteGrpcService";
import { fx_esMetodoManual } from "../../../../modulos/aplicaciones/response/AplicacionResponse";
import { cultivoGrpcCatalogService } from "../../../../modulos/catalogos/aplicaciones/services/CultivoGrpcCatalogService";
import { CultivoItem } from "../../../../generated/grpc/catalogos_aplicaciones_pb";
import { cultivoTratamientoGrpcCatalogService } from "../../../../modulos/catalogos/aplicaciones/services/CultivoTratamientoGrpcCatalogService";
import { CultivoTratamientoItem } from "../../../../generated/grpc/catalogos_aplicaciones_pb";
import { toISODate } from "../../../../utils/fechaUtils";
import { IFormBodyRender } from "../../../definitions/form/IFormBodyRender";
import { IFormField } from "../../../definitions/form/IFormField";
import { InputDateField } from "../../../implementations/form/date/InputDateField";
import { InputFileField } from "../../../implementations/form/file/InputFileField";
import { InputNumberField } from "../../../implementations/form/number/InputNumberField";
import { InputSelectField } from "../../../implementations/form/select/InputSelectField";
import { TextField } from "../../../implementations/form/text/TextField";
import { AplicacionLotesField } from "./AplicacionLotesField";
import { AplicacionMetodoField } from "./AplicacionMetodoField";
import { AplicacionProductosField } from "./AplicacionProductosField";
import template from "./aplicacionFormTemplate.html";

mustache.parse(template);

/** Contador incremental de instancias, para no colisionar ids en el DOM. */
let FORM_ID = 0;

/**
 * Formulario de aplicación (Render/Connect).
 *
 * Lo primero es el **método** (dron, estacionaria, espalda, tractor, drench):
 * es lo que decide cómo se mide la aplicación y, por lo tanto, qué pide el resto
 * del formulario. Por eso el formulario no es una lista fija de campos sino dos
 * bloques que se rearman al cambiarlo:
 *
 * - `#apCfg` — por dron, el **ZIP del vuelo**; por los manuales, el recipiente,
 *   el responsable y el campo propio del método (mezcla por planta en drench,
 *   capacidad de la bomba en espalda, equipo en estacionaria y tractor).
 * - La card de **lotes aplicados**, que solo existe en los métodos manuales —en
 *   dron el vuelo ya dice por dónde pasó—.
 *
 * Rearmar es destruir y reconstruir los campos (`_rearmarPorMetodo`), no
 * esconderlos: un campo oculto sigue validando, y "Registrar" se quedaría
 * trabado pidiendo un ZIP que ya no está en pantalla.
 */
export class AplicacionFormRender implements IFormBodyRender {
  /** Id de instancia, para los ids del DOM. */
  private readonly _id: number = ++FORM_ID;

  private readonly _request: AplicacionRequest;

  // === Siempre visibles ===
  private readonly _metodo: AplicacionMetodoField;
  private readonly _fecha: InputDateField<AplicacionRequest>;
  private readonly _cultivo: InputSelectField<AplicacionRequest, CultivoItem>;
  private readonly _tratamiento: InputSelectField<
    AplicacionRequest,
    CultivoTratamientoItem
  >;
  private readonly _productos: AplicacionProductosField;
  private readonly _lotes: AplicacionLotesField;

  /** Campos que dependen del método (se reconstruyen al cambiarlo). */
  private _camposMetodo: IFormField[] = [];

  constructor(request: AplicacionRequest) {
    this._request = request;
    this._metodo = new AplicacionMetodoField(request, () =>
      this._rearmarPorMetodo(true),
    );
    this._fecha = this._createFecha();
    this._cultivo = this._createCultivo();
    this._tratamiento = this._createTratamiento();
    // El gráfico de producto por lote se alimenta de estas dosis: cambiar una
    // tiene que repintarlo, igual que cambiar una cantidad de la tabla.
    this._productos = new AplicacionProductosField(request, () => {
      if (this._lotesMontados) this._lotes.refresh();
    });
    this._lotes = new AplicacionLotesField(request, () =>
      this._getParametros(),
    );
  }

  render(): string {
    return mustache.render(template, {
      id: this._id,
      metodo: this._metodo.render(),
      fecha: this._fecha.render(),
      cultivo: this._cultivo.render(),
      tratamiento: this._tratamiento.render(),
      productos: this._productos.render(),
      lotes: this._lotes.render(),
    });
  }

  connect(): void {
    this._metodo.connect();
    this._fecha.connect();
    this._cultivo.connect();
    this._tratamiento.connect();
    this._productos.connect();

    this._cfgEl = document.getElementById(`apCfg-${this._id}`);
    this._lotesCardEl = document.getElementById(`apLotesCard-${this._id}`);
    this._dosisHintEl = document.getElementById(`apDosisHint-${this._id}`);
    this._lotesTituloEl = document.getElementById(`apLotesTitulo-${this._id}`);
    this._lotesHintEl = document.getElementById(`apLotesHint-${this._id}`);

    // Los recipientes se piden una sola vez: el selector los necesita para
    // ofrecerlos y el cálculo para saber cuántos litros tiene el elegido.
    void this._cargarRecipientesAsync();
    // Sin limpiar: al abrir en edicion, el request ya trae lo del metodo guardado.
    this._rearmarPorMetodo(false);
  }

  dispose(): void {
    this._metodo.dispose();
    this._fecha.dispose();
    this._cultivo.dispose();
    this._tratamiento.dispose();
    this._productos.dispose();
    this._disposeCamposMetodo();
    this._lotes.dispose();
    this._lotesMontados = false;
  }

  /** Valida todos los campos visibles; retorna `true` si son válidos. */
  validate(): boolean {
    let isValid = true;
    this._getCamposActivos().forEach((field) => {
      field.showError();
      if (field.getError()) {
        if (isValid) field.focus();
        isValid = false;
      }
    });
    return isValid;
  }

  /** Contenedor de los campos que dependen del método. */
  private _cfgEl: HTMLElement | null = null;
  /** Card de lotes aplicados (se muestra solo en los métodos manuales). */
  private _lotesCardEl: HTMLElement | null = null;
  /** Subtítulo de la card de productos: dice en qué unidad va la dosis. */
  private _dosisHintEl: HTMLElement | null = null;
  /** Título de la card de lotes: cambia a "Plantas por lote" en drench. */
  private _lotesTituloEl: HTMLElement | null = null;
  /** Subtítulo de la card de lotes: en drench no se aplican decimales. */
  private _lotesHintEl: HTMLElement | null = null;
  /** Si la card de lotes está montada (conectada al DOM). */
  private _lotesMontados = false;
  /** Recipientes del cliente, para el selector y para resolver sus litros. */
  private _recipientes: AplicacionRecipienteResponse[] = [];
  private _disposed = false;

  /** Campos que participan de la validación en el método actual. */
  private _getCamposActivos(): IFormField[] {
    const campos: IFormField[] = [
      this._fecha,
      this._cultivo,
      this._tratamiento,
      ...this._camposMetodo,
      this._productos,
    ];
    if (fx_esMetodoManual(this._request.metodo)) campos.push(this._lotes);
    return campos;
  }

  /** Con qué se está midiendo, para el cálculo de la tabla de lotes. Los litros
   *  salen del recipiente **elegido ahora**: mientras se edita no hay valor
   *  congelado todavía (el server lo congela al guardar). */
  private _getParametros(): ParametrosMedicion {
    const recipiente = this._recipientes.find(
      (r) => r.id === this._request.recipienteId,
    );
    return {
      metodo: this._request.metodo,
      litrosRecipiente: recipiente?.litros ?? 0,
      mezclaPorPlantaMl: this._request.mezclaPorPlantaMl ?? 0,
    };
  }

  private async _cargarRecipientesAsync(): Promise<void> {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    try {
      this._recipientes = await fx_createAplicacionRecipienteGrpcService(
        finca.clienteId,
      ).getListAsync();
      if (this._disposed) return;
      // La tabla de lotes se pintó sin saber los litros del recipiente: recién
      // ahora puede calcular la mezcla y la descarga.
      if (this._lotesMontados) this._lotes.refresh();
    } catch {
      // Sin recipientes el selector queda vacío y la validación no deja
      // guardar, que es el comportamiento correcto: no hay con qué medir.
    }
  }

  /**
   * Reconstruye los campos propios del método y muestra u oculta la card de
   * lotes. Lo llama `connect()` y cada clic en los chips.
   *
   * @param limpiar Borra del request lo que el método anterior había cargado.
   *                Va en `true` solo cuando el cambio lo hizo el usuario: al
   *                montar el formulario en edición, el request **ya trae** lo
   *                del método guardado y limpiarlo lo borraría antes de verlo.
   */
  private _rearmarPorMetodo(limpiar: boolean): void {
    const esManual = fx_esMetodoManual(this._request.metodo);
    if (limpiar) this._limpiarMetodoAnterior(esManual);

    this._disposeCamposMetodo();
    this._camposMetodo = esManual
      ? this._createCamposManuales()
      : [this._createZip()];

    if (this._cfgEl) {
      this._cfgEl.innerHTML = this._camposMetodo
        .map((campo) => campo.render())
        .join("");
      this._camposMetodo.forEach((campo) => campo.connect());
    }

    // La dosis cambia de denominador con el método: por hectárea la calcula el
    // vuelo, por recipiente la prepara quien llena la caneca.
    this._productos.setEtiquetaDosis(
      esManual ? "Dosis x Recipiente" : "Dosis x Ha",
    );
    if (this._dosisHintEl) {
      this._dosisHintEl.textContent = esManual
        ? "Dosis por recipiente"
        : "Dosis por hectárea";
    }
    const esDrench = this._request.metodo === "drench";
    if (this._lotesTituloEl) {
      this._lotesTituloEl.textContent = esDrench
        ? "Plantas por lote"
        : "Cantidad por lote";
    }
    if (this._lotesHintEl) {
      // En drench se cuentan plantas, que son enteras: prometer decimales ahí
      // sería ofrecer media planta.
      this._lotesHintEl.textContent = esDrench
        ? "Plantas tratadas en cada lote"
        : "Cantidad aplicada en cada lote · acepta decimales";
    }

    this._toggleLotes(esManual);
  }

  /**
   * Borra del request lo que ya no corresponde al método elegido. Es el espejo
   * de lo que hace el server al guardar (`AplicarDronAsync`/`AplicarManualAsync`
   * limpian la rama contraria), y lo hace acá además para que la pantalla no
   * muestre después un dato que ya no aplica: si se elige el ZIP, se pasa a
   * espalda y se vuelve a dron, el archivo subido sigue ahí sin que se vea.
   */
  private _limpiarMetodoAnterior(esManual: boolean): void {
    if (esManual) {
      this._request.vueloUrl = null;
      // Tambien entre dos metodos manuales: la capacidad de la bomba es de
      // espalda y la mezcla por planta es de drench. Sin esto, pasar de espalda
      // a drench guardaria una capacidad de bomba en una aplicacion de drench.
      // Los lotes NO se borran: son los mismos, lo que cambia es en que unidad
      // se anotan, y el campo los vuelve a pedir vacios.
      if (this._request.metodo !== "drench") this._request.mezclaPorPlantaMl = null;
      if (this._request.metodo !== "espalda") this._request.capacidadBombaLitros = null;
      if (this._request.metodo === "drench" || this._request.metodo === "espalda") {
        this._request.equipo = null;
      }
      return;
    }
    this._request.recipienteId = null;
    this._request.mezclaPorPlantaMl = null;
    this._request.capacidadBombaLitros = null;
    this._request.equipo = null;
    this._request.responsable = null;
    // `length = 0` y no un array nuevo: `AplicacionLotesField` guarda la
    // referencia a este mismo array.
    this._request.lotes.length = 0;
  }

  /** Monta o desmonta la card de lotes. Desmontarla —y no solo esconderla— es
   *  lo que evita que siga validando cuando el método es dron. */
  private _toggleLotes(visible: boolean): void {
    if (!this._lotesCardEl) return;
    this._lotesCardEl.style.display = visible ? "" : "none";

    if (visible && !this._lotesMontados) {
      this._lotes.connect();
      this._lotesMontados = true;
      return;
    }
    if (visible) {
      // Ya montada: los números dependen del método y del recipiente.
      this._lotes.refresh();
    }
  }

  private _disposeCamposMetodo(): void {
    this._camposMetodo.forEach((campo) => campo.dispose());
    this._camposMetodo = [];
  }

  // ─── Campos por método ───

  /** Recipiente + campo propio del método + responsable. */
  private _createCamposManuales(): IFormField[] {
    const campos: IFormField[] = [this._createRecipiente()];

    switch (this._request.metodo) {
      case "drench":
        campos.push(this._createMezclaPorPlanta());
        break;
      case "espalda":
        campos.push(this._createCapacidadBomba());
        break;
      default:
        campos.push(this._createEquipo());
        break;
    }

    campos.push(this._createResponsable());
    return campos;
  }

  /** ZIP del vuelo (se sube al storage; requerido solo al crear). */
  private _createZip(): InputFileField<AplicacionRequest> {
    return new InputFileField(
      this._request,
      {
        label: "ZIP aplicación",
        description: "Archivo ZIP con los datos del vuelo",
        accept: ".zip",
        get: (r) => r.vueloUrl,
        set: (r, v) => {
          r.vueloUrl = v;
        },
      },
      { isRequired: this._request.id == null },
    );
  }

  /** Recipiente con el que se mide (requerido en los métodos manuales). */
  private _createRecipiente(): InputSelectField<
    AplicacionRequest,
    AplicacionRecipienteResponse
  > {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    return new InputSelectField(
      this._request,
      {
        label: "Recipiente",
        description:
          "Con qué se mide la aplicación. Sus litros convierten las cantidades de cada lote en mezcla.",
        emptyLabel: "Recipiente",
        service: fx_createAplicacionRecipienteGrpcService(finca.clienteId),
        get: (r) => r.recipienteId ?? "",
        set: (r, v) => {
          r.recipienteId = v || null;
          // Cambiar de caneca cambia la mezcla de todos los lotes.
          if (this._lotesMontados) this._lotes.refresh();
        },
        // Los litros en la etiqueta: son el dato que importa al elegir, y
        // evitan tener que acordarse de cuánto le cabe a cada uno.
        getLabel: (recipiente) =>
          `${recipiente.nombre} · ${recipiente.litros} L`,
      },
      { isRequired: true },
    );
  }

  /** Mezcla por planta en mililitros (solo drench, requerido). */
  private _createMezclaPorPlanta(): InputNumberField<AplicacionRequest> {
    return new InputNumberField(
      this._request,
      {
        label: "Mezcla por planta (ml)",
        description: "Mililitros que recibe cada planta. Convierte las plantas de cada lote en litros.",
        placeholder: "250",
        decimal: true,
        get: (r) => r.mezclaPorPlantaMl ?? 0,
        set: (r, v) => {
          r.mezclaPorPlantaMl = v > 0 ? v : null;
          if (this._lotesMontados) this._lotes.refresh();
        },
      },
      { isRequired: true, min: 1 },
    );
  }

  /** Capacidad de la bomba de espalda (opcional, informativa). */
  private _createCapacidadBomba(): InputNumberField<AplicacionRequest> {
    return new InputNumberField(
      this._request,
      {
        label: "Capacidad bomba (L)",
        description: "Litros de la bomba de espalda. Informativo: la mezcla se calcula con los recipientes.",
        placeholder: "20",
        decimal: true,
        get: (r) => r.capacidadBombaLitros ?? 0,
        set: (r, v) => {
          r.capacidadBombaLitros = v > 0 ? v : null;
        },
      },
      { min: 0 },
    );
  }

  /** Equipo con el que se aplicó (opcional, texto libre). */
  private _createEquipo(): TextField<AplicacionRequest> {
    return new TextField(
      this._request,
      {
        label: "Equipo",
        type: "text",
        description: "Con qué equipo se aplicó. Opcional.",
        get: (r) => r.equipo ?? "",
        set: (r, v) => {
          r.equipo = v.trim() || null;
        },
      },
      { maxLength: 80 },
    );
  }

  /** Quién aplicó (requerido en los métodos manuales). */
  private _createResponsable(): TextField<AplicacionRequest> {
    return new TextField(
      this._request,
      {
        label: "Responsable",
        type: "text",
        description: "Nombre del responsable o de la cuadrilla.",
        get: (r) => r.responsable ?? "",
        set: (r, v) => {
          r.responsable = v.trim() || null;
        },
      },
      { isRequired: true, maxLength: 120 },
    );
  }

  // ─── Campos comunes ───

  /** Fecha de la aplicación (máximo hoy). */
  private _createFecha(): InputDateField<AplicacionRequest> {
    return new InputDateField(this._request, {
      label: "Fecha aplicación",
      description: "Fecha en que se realizó la aplicación",
      max: toISODate(new Date()),
      get: (r) => r.fechaAplicacion,
      set: (r, v) => {
        r.fechaAplicacion = v;
      },
    });
  }

  /** Cultivo (catálogo, requerido). */
  private _createCultivo(): InputSelectField<AplicacionRequest, CultivoItem> {
    return new InputSelectField(
      this._request,
      {
        label: "Cultivo",
        description: "Cultivo sobre el que se realiza la aplicación",
        emptyLabel: "Cultivo",
        service: cultivoGrpcCatalogService,
        get: (r) => r.cultivoId,
        set: (r, v) => {
          r.cultivoId = v ?? "";
        },
        getLabel: (cultivo) => cultivo.nombre,
      },
      { isRequired: true },
    );
  }

  /** Tratamiento del cultivo (catálogo, requerido). */
  private _createTratamiento(): InputSelectField<
    AplicacionRequest,
    CultivoTratamientoItem
  > {
    return new InputSelectField(
      this._request,
      {
        label: "Tratamiento",
        description: "Tratamiento aplicado al cultivo",
        emptyLabel: "Tratamiento",
        service: cultivoTratamientoGrpcCatalogService,
        get: (r) => r.cultivoTratamientoId,
        set: (r, v) => {
          r.cultivoTratamientoId = v ?? "";
        },
        getLabel: (tratamiento) => tratamiento.tratamiento,
      },
      { isRequired: true },
    );
  }
}
